import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import {
  Users,
  Key,
  Shield,
  Activity,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  ImageOff
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(7);

  const fetchData = React.useCallback(async () => {
    try {
      const [dashboardResponse, statsResponse] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getStats(selectedPeriod)
      ]);

      setDashboardData(dashboardResponse.data.data);
      setStats(statsResponse.data.data);
    } catch (error) {
      console.error('Error fetching admin dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatChartData = () => {
    if (!stats?.dailyStats) return [];

    return stats.dailyStats.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      requests: item.totalRequests || 0,
      users: item.activeUsers || 0,
      errors: item.errorCount || 0
    }));
  };

  const getUserStatusData = () => {
    if (!dashboardData?.userStats) return [];
    
    return [
      { name: 'Active', value: dashboardData.userStats.activeUsers, color: '#10B981' },
      { name: 'Inactive', value: dashboardData.userStats.inactiveUsers, color: '#EF4444' },
      { name: 'Admin', value: dashboardData.userStats.adminUsers, color: '#3B82F6' }
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const chartData = formatChartData();
  const userStatusData = getUserStatusData();

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Shield className="w-8 h-8 mr-3 text-red-600" />
              Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-2">
              System overview and management tools
            </p>
          </div>
          
          {/* Period Selector */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Period:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
              className="rounded border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value={1}>Today</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.userStats?.totalUsers || 0}</p>
              <p className="text-xs text-gray-500">{dashboardData?.userStats?.activeUsers || 0} active</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <Key className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">API Keys</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.apiKeyStats?.totalKeys || 0}</p>
              <p className="text-xs text-gray-500">{dashboardData?.apiKeyStats?.activeKeys || 0} active</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Today's Requests</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.requestStats?.requestsToday || 0}</p>
              <p className="text-xs text-gray-500">{Math.round(((dashboardData?.requestStats?.successToday || 0) / (dashboardData?.requestStats?.requestsToday || 1)) * 100)}% success</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Blocked IPs</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.securityStats?.blockedIps || 0}</p>
              <p className="text-xs text-gray-500">Active blocks</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Activity Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">API Activity</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="requests" stroke="#3B82F6" name="Requests" strokeWidth={2} />
                <Line type="monotone" dataKey="users" stroke="#10B981" name="Active Users" strokeWidth={2} />
                <Line type="monotone" dataKey="errors" stroke="#EF4444" name="Errors" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No activity data available</p>
              </div>
            </div>
          )}
        </div>

        {/* User Status Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">User Status Distribution</h3>
          {userStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={userStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {userStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No user data available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions & System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Quick Actions */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <a
              href="/admin/users"
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <Users className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <p className="font-medium">Manage Users</p>
                  <p className="text-sm text-gray-600">View and manage user accounts</p>
                </div>
              </div>
            </a>
            
            <a
              href="/admin/api-keys"
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <Key className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <p className="font-medium">API Keys</p>
                  <p className="text-sm text-gray-600">Monitor API key usage</p>
                </div>
              </div>
            </a>
            
            <a
              href="/admin/blocked-ips"
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <Shield className="w-8 h-8 text-red-600 mr-3" />
                <div>
                  <p className="font-medium">IP Blocking</p>
                  <p className="text-sm text-gray-600">Manage blocked IP addresses</p>
                </div>
              </div>
            </a>
            
            <a
              href="/admin/missing-images"
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <ImageOff className="w-8 h-8 text-orange-600 mr-3" />
                <div>
                  <p className="font-medium">Missing Images</p>
                  <p className="text-sm text-gray-600">Cards without CDN images</p>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Recent Activity / Alerts */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">System Health</h3>
          <div className="space-y-4">
            <div className="flex items-center p-3 bg-green-50 rounded">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              <div>
                <p className="text-sm font-medium">Database Connection</p>
                <p className="text-xs text-gray-600">All databases operational</p>
              </div>
            </div>
            
            <div className="flex items-center p-3 bg-green-50 rounded">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              <div>
                <p className="text-sm font-medium">Redis Cache</p>
                <p className="text-xs text-gray-600">Cache server running normally</p>
              </div>
            </div>
            
            {stats?.totalStats?.errors > 0 && (
              <div className="flex items-center p-3 bg-yellow-50 rounded">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mr-3" />
                <div>
                  <p className="text-sm font-medium">Error Rate Alert</p>
                  <p className="text-xs text-gray-600">{stats.totalStats.errors} errors in the last {selectedPeriod} days</p>
                </div>
              </div>
            )}

            <div className="flex items-center p-3 bg-blue-50 rounded">
              <TrendingUp className="w-4 h-4 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium">API Performance</p>
                <p className="text-xs text-gray-600">
                  Avg response time: {Math.round(stats?.totalStats?.avgResponseTime || 0)}ms
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top API Keys Today */}
      {dashboardData?.topApiKeys?.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Top API Keys Today</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    API Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Requests Today
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Success Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.topApiKeys.map((apiKey, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{apiKey.username || apiKey.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">{apiKey.keyPrefix}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {apiKey.requestsToday || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {apiKey.requestsToday ? Math.round((apiKey.successToday / apiKey.requestsToday) * 100) : 0}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        apiKey.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {apiKey.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;